import React, { useState } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { TextField, TextArea, SmallBtn } from "@/components/praticien/fiche/ui";

const uid = () => Math.random().toString(36).slice(2, 9);

// Section 10 — Programme personnalisé (réorganisable).
export const Programme = ({ value, onChange, T }) => {
  const list = value || [];
  const [label, setLabel] = useState("");

  const add = () => {
    const l = label.trim();
    if (!l) return;
    onChange([...list, { id: uid(), activite: l, objectif: "", consignes: "" }]);
    setLabel("");
  };
  const update = (id, patch) => onChange(list.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  const remove = (id) => onChange(list.filter((a) => a.id !== id));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const arr = [...list];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={T.activityLabel} data-testid="fiche-programme-input"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          className="flex-1 rounded-xl border border-[var(--border)] bg-white/80 px-3.5 py-2 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]" />
        <SmallBtn onClick={add} testid="fiche-programme-add" tone="solid"><Plus size={14} /> {T.addActivity}</SmallBtn>
      </div>

      {list.length === 0 ? (
        <p className="text-sm text-[var(--ink-soft)] italic">{T.programmeEmpty}</p>
      ) : (
        <div className="space-y-3">
          {list.map((a, i) => (
            <div key={a.id} className="rounded-2xl border border-[var(--border)] bg-white/70 p-4" data-testid={`fiche-programme-${i}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="flex items-center justify-center h-7 w-7 rounded-full bg-[var(--gold)] text-white text-sm shrink-0">{i + 1}</span>
                <input value={a.activite} onChange={(e) => update(a.id, { activite: e.target.value })} data-testid={`fiche-programme-label-${i}`}
                  className="flex-1 rounded-xl border border-[var(--border)] bg-white/80 px-3 py-1.5 text-sm font-medium text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]" />
                <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`fiche-programme-up-${i}`} className={`h-8 w-8 rounded-lg flex items-center justify-center border ${i === 0 ? "border-[var(--border)] text-[var(--beige)]" : "border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white"}`}><ChevronUp size={16} /></button>
                <button onClick={() => move(i, 1)} disabled={i === list.length - 1} data-testid={`fiche-programme-down-${i}`} className={`h-8 w-8 rounded-lg flex items-center justify-center border ${i === list.length - 1 ? "border-[var(--border)] text-[var(--beige)]" : "border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white"}`}><ChevronDown size={16} /></button>
                <button onClick={() => remove(a.id)} data-testid={`fiche-programme-remove-${i}`} className="text-[#B0553B] hover:opacity-70"><Trash2 size={16} /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-9">
                <TextArea label={T.activityObjectif} value={a.objectif} onChange={(v) => update(a.id, { objectif: v })} testid={`fiche-programme-objectif-${i}`} rows={2} />
                <TextArea label={T.activityConsignes} value={a.consignes} onChange={(v) => update(a.id, { consignes: v })} testid={`fiche-programme-consignes-${i}`} rows={2} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Section 4 — Objectifs du suivi.
export const Objectifs = ({ value, onChange, lang, T, PRIORITES }) => {
  const items = value.items || [];
  const add = () => onChange({ ...value, items: [...items, { id: uid(), therapeutique: "", activites: "", priorite: "moyenne" }] });
  const update = (id, patch) => onChange({ ...value, items: items.map((o) => (o.id === id ? { ...o, ...patch } : o)) });
  const remove = (id) => onChange({ ...value, items: items.filter((o) => o.id !== id) });

  return (
    <div className="space-y-4">
      <TextArea label={T.objGeneral} value={value.general} onChange={(v) => onChange({ ...value, general: v })} testid="fiche-obj-general" rows={2} />
      {items.map((o, i) => (
        <div key={o.id} className="rounded-2xl border border-[var(--border)] bg-white/70 p-4 space-y-3" data-testid={`fiche-obj-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-[var(--gold)]">{T.objTherapeutique} {i + 1}</span>
            <button onClick={() => remove(o.id)} data-testid={`fiche-obj-remove-${i}`} className="text-[#B0553B] hover:opacity-70"><Trash2 size={16} /></button>
          </div>
          <TextArea value={o.therapeutique} onChange={(v) => update(o.id, { therapeutique: v })} testid={`fiche-obj-ther-${i}`} placeholder={T.objTherapeutique} rows={2} />
          <TextArea value={o.activites} onChange={(v) => update(o.id, { activites: v })} testid={`fiche-obj-act-${i}`} placeholder={T.activitesATravailler} rows={2} />
          <div>
            <label className="block text-sm font-medium text-[var(--ink)] mb-1.5">{T.priorite}</label>
            <select value={o.priorite} onChange={(e) => update(o.id, { priorite: e.target.value })} data-testid={`fiche-obj-priorite-${i}`}
              className="rounded-xl border border-[var(--border)] bg-white/80 px-3 py-2 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]">
              {PRIORITES.map((p) => (<option key={p.key} value={p.key}>{p[lang]}</option>))}
            </select>
          </div>
        </div>
      ))}
      <SmallBtn onClick={add} testid="fiche-obj-add"><Plus size={14} /> {T.addObjectif}</SmallBtn>
    </div>
  );
};
